import {FC} from 'react';
import {Box, Grid} from '@mui/material';
import {Form, Formik, FormikConfig, FormikProps} from 'formik';
import {useIntl} from 'react-intl';
import {businessesApi, companyApi, employeeApi} from 'src/api';
import {useBusinessId, useAppDispatch, useAppSelector} from 'src/app/hooks';
import {AppDispatch, store} from 'src/app/store';
import {AMPFormSelect} from 'src/components/common/AMPFormSelect';
import {AMPInput} from 'src/components/common/AMPInput';
import {CancelButton} from 'src/components/common/CancelButton';
import {SubmitButton} from 'src/components/common/SubmitButton';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {getHelperText} from 'src/utils/forms';
import {isErrorObject, processedLanguages, valuesTrimmer} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {titles} from '../formData/common/initialData';
import {ConsumptionPaymentMode, LanguageEnum, TitleEnum} from '../../../enums';
import {actionsWrapperSx, formSx} from './EmployeeForm.sx';
import {EmployeeFormTypes} from './EmployeeForm.types';
import {EmployeeCreateRequest, UpdateEmployeeRequest} from 'src/types/employee';
import * as Yup from 'yup';

export const employeeFormSchema = Yup.object().shape({
    firstName: Yup.string().max(255).required('schemaRequiredFirstName'),
    lastName: Yup.string().max(255).required('schemaRequiredLastName'),
    email: Yup.string().email('schemaValidEmail').max(255).required('schemaRequiredEmail'),
    title: Yup.string().required('schemaRequiredTitle'),
    companyId: Yup.number().required('schemaRequiredCompanyId'),
    consumptionPaymentMode: Yup.string().required('schemaRequiredPaymentMode'),
});

export const employeeEditFormSchema = Yup.object().shape({
    firstName: Yup.string().max(255).required('schemaRequiredFirstName'),
    lastName: Yup.string().max(255).required('schemaRequiredLastName'),
    title: Yup.string().required('schemaRequiredTitle'),
    consumptionPaymentMode: Yup.string().required('schemaRequiredPaymentMode'),
});

export interface EmployeeFormProps extends Omit<EmployeeCreateRequest, 'businessId'> {
}

export interface EmployeeEditFormProps extends Omit<UpdateEmployeeRequest, 'businessId' | 'companyId'> {
}

type AddEmployeeMutationTrigger = ReturnType<typeof employeeApi.useCreateNewEmployeeMutation>[0];
type BusinessesQueryTrigger = ReturnType<typeof businessesApi.useLazyGetBusinessesQuery>[0];

const handleSubmit =
    (
        trigger: AddEmployeeMutationTrigger,
        dispatch: AppDispatch,
        handleClose: () => void,
        businessId: number,
        companyId: number
    ): FormikConfig<EmployeeFormProps>['onSubmit'] =>
        async (values) => {
            try {
                dispatch(preloaderChangeStatus(true));

                const value = valuesTrimmer(values);

                await trigger({
                    ...value,
                    businessId,
                    companyId: companyId ? companyId : values.companyId,
                }).unwrap();

                handleClose();
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                dispatch(preloaderChangeStatus(false));
            }
        };

export const getBusinessesData = async (trigger: BusinessesQueryTrigger) => {
    try {
        store.dispatch(preloaderChangeStatus(true));
        await trigger({
            params: {
                size: 2000,
            },
        });
    } catch (error) {
        if (isErrorObject(error)) {
            NotifyError(error.data.message);
        }
    } finally {
        store.dispatch(preloaderChangeStatus(false));
    }
};

export const EmployeeForm: FC<EmployeeFormTypes> = ({
                                                        handleClose,
                                                        isEditMode = false,
                                                        initialValues: propInitialValues,
                                                        onSubmit: propOnSubmit,
                                                        onCancel,
                                                        preselectedCompanyId,
                                                        hidePaymentMode = false,
                                                    }) => {
    const intl = useIntl();

    const {authInfo} = useAppSelector((state) => state.common);
    const businessId = useBusinessId();
    const companyId = authInfo?.company?.id;

    const isCompanyUser = !!companyId;
    const hasPreselectedCompany = !!preselectedCompanyId;

    const {data: companiesData} = companyApi.useSearchCompaniesQuery(
        {
            data: {
                businessId: businessId,
            },
            params: {
                size: 1_000,
                page: 0,
            },
        },
        {
            skip: isCompanyUser || isEditMode,
        }
    );

    const dispatch = useAppDispatch();

    const [createNewEmployeeTrigger] = employeeApi.useCreateNewEmployeeMutation();

    const defaultInitialValues = {
        email: '',
        firstName: '',
        lastName: '',
        title: TitleEnum.MISS,
        companyId: isCompanyUser ? companyId : (hasPreselectedCompany ? preselectedCompanyId : null),
        consumptionPaymentMode: ConsumptionPaymentMode.COMPANY,
    };

    const editInitialValues = propInitialValues || {
        firstName: '',
        lastName: '',
        title: TitleEnum.MISS,
        consumptionPaymentMode: ConsumptionPaymentMode.COMPANY,
        language: LanguageEnum.ENGLISH,
    };

    return (
        <Formik
            initialValues={isEditMode ? editInitialValues : defaultInitialValues}
            validationSchema={isEditMode ? employeeEditFormSchema : employeeFormSchema}
            onSubmit={isEditMode && propOnSubmit ? propOnSubmit as any : handleSubmit(
                createNewEmployeeTrigger,
                dispatch,
                handleClose,
                businessId,
                companyId || preselectedCompanyId
            )}
        >
            {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  isSubmitting,
                  touched,
                  values,
              }: FormikProps<any>) => (
                <Form
                    onSubmit={handleSubmit}
                    noValidate
                >
                    <Box sx={formSx}>
                        <Grid
                            container
                            spacing={2}
                        >
                            {/* Company */}
                            {!isCompanyUser && !hasPreselectedCompany && !isEditMode && (
                                <Grid
                                    xs={12}
                                    item
                                >
                                    <AMPFormSelect
                                        data={
                                            (companiesData?.content ?? []).map((company) => ({
                                                id: company.id as number,
                                                name: company.name as string,
                                                value: company.id as number,
                                            })) ?? []
                                        }
                                        value={(values as any).companyId}
                                        label={'companyTabForRfidDialog'}
                                        input={'companyId'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={(touched as any)['companyId'] && Boolean((errors as any)['companyId'])}
                                        helperText={getHelperText('companyId', touched as any, errors as any, intl)}
                                    />
                                </Grid>
                            )}

                            {/* Title */}
                            <Grid
                                xs={12}
                                md={6}
                                item
                            >
                                <AMPFormSelect
                                    data={titles(intl)}
                                    value={values.title}
                                    label={'title'}
                                    input={'title'}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    hasError={touched['title'] && Boolean(errors['title'])}
                                    helperText={getHelperText('title', touched, errors, intl)}
                                />
                            </Grid>

                            {/* Email address */}
                            {!isEditMode ? (
                                <Grid
                                    xs={12}
                                    md={6}
                                    item
                                >
                                    <AMPInput
                                        value={(values as any).email}
                                        type={'emailAddress'}
                                        label={'emailAddress'}
                                        input={'email'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={(touched as any).email && Boolean((errors as any).email)}
                                        helperText={getHelperText('email', touched as any, errors as any, intl)}
                                    />
                                </Grid>
                            ) : (
                                <Grid
                                    xs={12}
                                    md={6}
                                    item
                                >
                                    {/* Empty Grid to preserve layout */}
                                </Grid>
                            )}

                            {/* First Name */}
                            <Grid
                                xs={12}
                                md={6}
                                item
                            >
                                <AMPInput
                                    value={values.firstName}
                                    type={'firstName'}
                                    label={'firstName'}
                                    input={'firstName'}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    hasError={touched['firstName'] && Boolean(errors['firstName'])}
                                    helperText={getHelperText('firstName', touched, errors, intl)}
                                />
                            </Grid>

                            {/* Last Name */}
                            <Grid
                                xs={12}
                                md={6}
                                item
                            >
                                <AMPInput
                                    value={values.lastName}
                                    type={'lastName'}
                                    label={'lastName'}
                                    input={'lastName'}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    hasError={touched['lastName'] && Boolean(errors['lastName'])}
                                    helperText={getHelperText('lastName', touched, errors, intl)}
                                />
                            </Grid>

                            {/* Language - editable once they exist; at creation it follows the company */}
                            {isEditMode && (
                                <Grid
                                    xs={12}
                                    item
                                >
                                    <AMPFormSelect
                                        data={processedLanguages(intl)}
                                        value={values.language ?? LanguageEnum.ENGLISH}
                                        label={'language'}
                                        input={'language'}
                                        onChange={handleChange}
                                    />
                                </Grid>
                            )}

                            {/* Payment Mode - hidden for EMPLOYEE role */}
                            {!hidePaymentMode && (
                                <Grid
                                    xs={12}
                                    item
                                >
                                    <AMPFormSelect
                                        data={[
                                            {
                                                id: 1,
                                                name: intl.formatMessage({id: 'paymentModeCompanyStatus'}),
                                                value: ConsumptionPaymentMode.COMPANY,
                                            },
                                            {
                                                id: 2,
                                                name: intl.formatMessage({id: 'paymentModeEmployeeStatus'}),
                                                value: ConsumptionPaymentMode.EMPLOYEE,
                                            },
                                        ]}
                                        value={values.consumptionPaymentMode}
                                        label={'paymentModeColumnHeader'}
                                        input={'consumptionPaymentMode'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={touched['consumptionPaymentMode'] && Boolean(errors['consumptionPaymentMode'])}
                                        helperText={getHelperText('consumptionPaymentMode', touched, errors, intl)}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Box>

                    <Box sx={actionsWrapperSx}>
                        <CancelButton
                            text="cancel"
                            handler={onCancel || handleClose}
                        />

                        <SubmitButton
                            text={isEditMode ? 'updateEmployee' : 'createEmployee'}
                            color="primary"
                            isSubmitting={isSubmitting}
                        />
                    </Box>
                </Form>
            )}
        </Formik>
    );
};
